/**
 * Provides a custom validator for RJSF that does not use the AJV validation library.
 * The type signatures of this class and methods generally follow the RJSF structure
 * set by the `validator-ajv8` package. This validator can run without requiring the
 * use of `eval` or `new Function`, which is useful when creating a more restrictive
 * Content Security Policy (CSP).
 *
 * See:
 *   - https://github.com/rjsf-team/react-jsonschema-form/blob/v5.17.1/packages/validator-ajv8/src/validator.ts
 *   - https://www.npmjs.com/package/@cfworker/json-schema
 *   - https://ajv.js.org/security.html#content-security-policy
 */
import { Validator } from '@cfworker/json-schema';
import {
  hashForSchema,
  ID_KEY,
  ROOT_SCHEMA_PREFIX,
  withIdRefPrefix,
} from '@rjsf/utils';
import metaSchemaDraft07 from './json-schema-draft-07.json';
import { processRawValidationErrors } from './error-transformer';

/**
 * A custom validator for RJSF that uses `@cfworker/json-schema` instead of the `ajv` default.
 * This validator can run without requiring the use of `eval` or `new Function`, which is
 * useful when creating a more restrictive Content Security Policy (CSP).
 *
 * See:
 *   - https://github.com/rjsf-team/react-jsonschema-form/blob/v5.17.1/packages/validator-ajv8/src/validator.ts
 *   - https://www.npmjs.com/package/@cfworker/json-schema
 *   - https://ajv.js.org/security.html#content-security-policy
 */
export default class CFWorkerFormValidator {
  // Optional additional validation schemas for the underlying JSON validator
  additionalMetaSchemas = [];

  // Optional transformation function to localize validation errors
  localizeErrors = (errors) => errors;

  /**
   * Constructs a new validator object that uses `@cfworker/json-schema` instead of AJV.
   *
   * @param [options] - options
   * @param [localizeErrors] - function that localizes validation errors
   */
  constructor(options, localizeErrors) {
    if (Array.isArray(options?.additionalMetaSchemas)) {
      this.additionalMetaSchemas = options.additionalMetaSchemas;
    }

    if (typeof localizeErrors === 'function') {
      this.localizeErrors = localizeErrors;
    }
  }

  /**
   * Validates `formData` against the provided `schema` while bypassing RJSF entirely.
   *
   * @param schema - used to validate form data
   * @param formData - form data to be validated
   */
  rawValidation(schema, formData) {
    let compilationError;
    let validationErrors;

    try {
      const compiledValidator = new Validator(schema, '7', false);

      this.additionalMetaSchemas.forEach((additionalSchema) => {
        compiledValidator.addSchema(additionalSchema);
      });

      const validationResult = compiledValidator.validate(formData);
      validationErrors = this.localizeErrors(validationResult.errors);
    } catch (err) {
      console.warn('Error encountered running the raw validation:', err);
      compilationError = err;
    }

    // The terminology here may seem confusing, but this is the existing contract set up
    // for the original ajv8 validator and what the calling code expects.
    return {
      errors: validationErrors,
      validationError: compilationError,
    };
  }

  /**
   * Validates form data using the provided schema and a variety of different options.
   * Custom validation errors can be added to each field. The resultant validation
   * errors can also be transformed as desired. By default, the `@cfworker/json-schema`
   * library validates the form data and transforms the output into a format that
   * RJSF expects. See `./error-transformer.js` for additional details concerning
   * error post-processing.
   *
   * @param schema - used to validate form data
   * @param formData - form data to be validated
   * @param [customValidate] - function that may be used to perform custom validation
   * @param [transformErrors] - function that may be used to transform errors after validation
   * @param [uiSchema] - schema option passed to `customValidate` and `transformErrors`
   */
  validateFormData(
    formData,
    schema,
    customValidate,
    transformErrors,
    uiSchema,
  ) {
    const rawErrors = this.rawValidation(schema, formData);
    return processRawValidationErrors(
      this,
      rawErrors,
      formData,
      schema,
      customValidate,
      transformErrors,
      uiSchema,
    );
  }

  /**
   * Validates form data against a custom schema. Returns true when the custom schema
   * is a valid JSON schema and the form data is a valid instance of the custom schema.
   *
   * @param schema - used to validate form data
   * @param formData - form data to be validated
   * @param rootSchema - used to handle models that only live in the root schema
   */
  isValid(schema, formData, rootSchema) {
    if (!this.#isValidSchema(schema, rootSchema)) {
      return false;
    }

    try {
      const schemaWithIdRefPrefix = withIdRefPrefix(schema);
      const compiledValidator = new Validator(schemaWithIdRefPrefix, '7', true);

      const schemaId =
        schemaWithIdRefPrefix[ID_KEY] ?? hashForSchema(schemaWithIdRefPrefix);
      compiledValidator.addSchema(schema, schemaId);

      const rootSchemaId = rootSchema[ID_KEY] ?? ROOT_SCHEMA_PREFIX;
      compiledValidator.addSchema(rootSchema, rootSchemaId);

      const result = compiledValidator.validate(formData);
      return result.valid;
    } catch (err) {
      console.warn('Error encountered running the validation:', err);
      return false;
    }
  }

  /**
   * Determines whether the provided schema is a valid JSON schema according
   * to Draft 7 of the standard.
   *
   * @param schema - schema to be validated against the JSON Draft 7 standard
   */
  #isValidSchema(schema, rootSchema) {
    try {
      const compiledValidator = new Validator(metaSchemaDraft07);
      const schemaResult = compiledValidator.validate(schema).valid;
      const rootSchemaResult = compiledValidator.validate(rootSchema).valid;

      return schemaResult && rootSchemaResult;
    } catch (err) {
      console.warn('Error encountered validating the schema formats:', err);
      return false;
    }
  }
}

export const defaultValidator = new CFWorkerFormValidator();
